import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import dayjs from "dayjs";
import { usePlace } from "./PlaceProvider";
import { HTTPClient } from "../HTTPClient";
import { createPageCache } from "../utils/pageCache";

export interface ChartPoint {
  name: string;
  current: number;
  previous: number;
}

interface StatsCache {
  startDate: string;
  endDate: string;
  participants: number | null;
  participantsTrend: number | undefined;
  participantsChartData: ChartPoint[];
  avis: number | null;
  avisTrend: number | undefined;
  avisChartData: ChartPoint[];
  igCount: number;
  ttCount: number;
  parrainageCount: number;
}

interface StatsContextValue {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  setStartDate: (d: dayjs.Dayjs) => void;
  setEndDate: (d: dayjs.Dayjs) => void;
  participants: number | null;
  participantsTrend: number | undefined;
  participantsChartData: ChartPoint[];
  avis: number | null;
  avisTrend: number | undefined;
  avisChartData: ChartPoint[];
  igCount: number;
  ttCount: number;
  parrainageCount: number;
}

const StatsContext = createContext<StatsContextValue>({
  startDate: dayjs().subtract(30, "day"),
  endDate: dayjs(),
  setStartDate: () => {},
  setEndDate: () => {},
  participants: null,
  participantsTrend: undefined,
  participantsChartData: [],
  avis: null,
  avisTrend: undefined,
  avisChartData: [],
  igCount: 0,
  ttCount: 0,
  parrainageCount: 0,
});

const _statsCache = createPageCache<StatsCache>();

const sum = (arr: { value: number }[]) => {
  return arr.reduce((a, d) => a + d.value, 0);
};
const toChartData = (
  curr: { name: string; value: number }[],
  prev: { name: string; value: number }[]
): ChartPoint[] =>
  curr.map((d, i) => ({ name: d.name, current: d.value, previous: prev[i]?.value ?? 0 }));

export function StatsProvider({ children }: { children: ReactNode }) {
  const { selectedPlace } = usePlace();

  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());

  const _c = _statsCache.get(selectedPlace?.id ?? "");

  const [participants, setParticipants] = useState<number | null>(_c?.participants ?? null);
  const [participantsTrend, setParticipantsTrend] = useState<number | undefined>(
    _c?.participantsTrend
  );
  const [participantsChartData, setParticipantsChartData] = useState<ChartPoint[]>(
    _c?.participantsChartData ?? []
  );
  const [avis, setAvis] = useState<number | null>(_c?.avis ?? null);
  const [avisTrend, setAvisTrend] = useState<number | undefined>(_c?.avisTrend);
  const [avisChartData, setAvisChartData] = useState<ChartPoint[]>(_c?.avisChartData ?? []);
  const [igCount, setIgCount] = useState<number>(_c?.igCount ?? 0);
  const [ttCount, setTtCount] = useState<number>(_c?.ttCount ?? 0);
  const [parrainageCount, setParrainageCount] = useState<number>(_c?.parrainageCount ?? 0);

  useEffect(() => {
    if (!selectedPlace?.id) return;

    const cacheKey = selectedPlace.id;
    const start = startDate.format("YYYY-MM-DD");
    const end = endDate.format("YYYY-MM-DD");
    if (_statsCache.isHit(cacheKey, start, end)) return;

    _statsCache.patch(cacheKey, { startDate: start, endDate: end });

    const periodDays = endDate.diff(startDate, "day") || 1;
    const statRangeToUse = "dailyStats"; // chart is not well formed for HOURLY. USE daily by default for now // periodDays === 1 ? "hourlyStats" : "dailyStats";
    const prevStart = startDate.subtract(periodDays, "day");
    const base = { id: selectedPlace.id };

    const patch = (data: Partial<StatsCache>) => _statsCache.patch(cacheKey, data);

    setParticipants(null);
    setAvis(null);

    Promise.all([
      HTTPClient.post("/users-stats/", { ...base, startDate, endDate }),
      HTTPClient.post("/users-stats/", { ...base, startDate: prevStart, endDate: startDate }),
      HTTPClient.post("/chartData/google", { ...base, startDate, endDate }),
      HTTPClient.post("/chartData/google", { ...base, startDate: prevStart, endDate: startDate }),
      HTTPClient.post("/stats/socials", { ...base, startDate, endDate, subType: "INSTAGRAM" }),
      HTTPClient.post("/stats/socials", { ...base, startDate, endDate, subType: "TIKTOK" }),
      HTTPClient.post("/chartData/referral", { ...base, startDate, endDate }),
    ])
      .then(([currPart, prevPart, currAvis, prevAvis, ig, tt, referral]) => {
        const partTotal = sum(currPart.data[statRangeToUse]);
        const prevPartTotal = sum(prevPart.data[statRangeToUse]);
        const partTrend = prevPartTotal
          ? Math.round(((partTotal - prevPartTotal) / prevPartTotal) * 10000) / 100
          : undefined;
        const partChart = toChartData(currPart.data[statRangeToUse], prevPart.data[statRangeToUse]);

        const avisTotal = sum(currAvis.data);
        const prevAvisTotal = sum(prevAvis.data);
        const avisTrendVal = prevAvisTotal
          ? Math.round(((avisTotal - prevAvisTotal) / prevAvisTotal) * 10000) / 100
          : undefined;
        const avisChart = toChartData(currAvis.data, prevAvis.data);

        const ig0 = ig.data?.[0]?.rate ?? 0;
        const tt0 = tt.data?.[0]?.rate ?? 0;
        const parr = sum(referral.data);

        setParticipants(partTotal);
        setParticipantsTrend(partTrend);
        setParticipantsChartData(partChart);
        setAvis(avisTotal);
        setAvisTrend(avisTrendVal);
        setAvisChartData(avisChart);
        setIgCount(ig0);
        setTtCount(tt0);
        setParrainageCount(parr);

        patch({
          participants: partTotal,
          participantsTrend: partTrend,
          participantsChartData: partChart,
          avis: avisTotal,
          avisTrend: avisTrendVal,
          avisChartData: avisChart,
          igCount: ig0,
          ttCount: tt0,
          parrainageCount: parr,
        });
      })
      .catch((e) => {
        console.error(e);
        setParticipants(0);
        setAvis(0);
      });
  }, [selectedPlace?.id, startDate, endDate]);

  return (
    <StatsContext.Provider
      value={{
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        participants,
        participantsTrend,
        participantsChartData,
        avis,
        avisTrend,
        avisChartData,
        igCount,
        ttCount,
        parrainageCount,
      }}
    >
      {children}
    </StatsContext.Provider>
  );
}

export function useStats() {
  return useContext(StatsContext);
}
